import React, { createContext, useContext, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store';
import tableStateReducer, {
  DEFAULTS,
  initializeTable,
  resetTable,
  setFilterModel,
  setPaginationModel,
  setSearchQuery,
  setSortModel,
} from '@/store/slices/tableStateSlice';
import { IFilterModel, IPaginationModel, ISortModel } from '@/types/admin/dataTable';

type DataTableContextValue = {
  tableKey: string | null;
};

const DataTableContext = createContext<DataTableContextValue>({ tableKey: null });

interface DataTableProviderProps {
  tableKey: string;
  children: React.ReactNode;
  defaults?: Partial<{
    searchQuery: string;
    filterModel: IFilterModel;
    sortModel: ISortModel[];
    paginationModel: IPaginationModel;
  }>;
  autoInitialize?: boolean;
}

export const DataTableProvider: React.FC<DataTableProviderProps> = ({
  tableKey,
  children,
  defaults,
  autoInitialize = true,
}) => {
  const dispatch = useAppDispatch();
  const hasExisting = useAppSelector((state) => Boolean((state as any).tableState?.[tableKey]));

  const hasInitialized = useRef(false);
  
  useEffect(() => {
    if (!autoInitialize || hasExisting || hasInitialized.current) return;
    hasInitialized.current = true;
    dispatch(initializeTable({ key: tableKey, ...defaults }));
  }, [dispatch, tableKey, autoInitialize, hasExisting]);

  const value = useMemo(() => ({ tableKey }), [tableKey]);
  return <DataTableContext.Provider value={value}>{children}</DataTableContext.Provider>;
};

export type UseTableStateResult = {
  tableKey: string;
  searchQuery: string;
  filterModel: IFilterModel;
  sortModel: ISortModel[];
  paginationModel: IPaginationModel;
  setSearch: (query: string) => void;
  setFilter: (model: IFilterModel) => void;
  setSort: (model: ISortModel[]) => void;
  setPagination: (model: IPaginationModel) => void;
  reset: () => void;
};

// Use table state from Redux; either via context-provided key or explicit override
export const useDataTableState = (tableKeyOverride?: string): UseTableStateResult => {
  const dispatch = useAppDispatch();
  const ctx = useContext(DataTableContext);
  const key = tableKeyOverride ?? ctx.tableKey ?? '';

  const tableSlice = useAppSelector((state) => (state as any).tableState?.[key]) || DEFAULTS;

  const setSearch = useCallback((query: string) => {
    dispatch(setSearchQuery({ key, searchQuery: query }));
  }, [dispatch, key]);

  const setFilter = useCallback((model: IFilterModel) => {
    dispatch(setFilterModel({ key, filterModel: model }));
  }, [dispatch, key]);

  const setSort = useCallback((model: ISortModel[]) => {
    dispatch(setSortModel({ key, sortModel: model }));
  }, [dispatch, key]);

  const setPagination = useCallback((model: IPaginationModel) => {
    dispatch(setPaginationModel({ key, paginationModel: model }));
  }, [dispatch, key]);

  const reset = useCallback(() => {
    dispatch(resetTable({ key }));
  }, [dispatch, key]);

  return useMemo(() => ({
    tableKey: key,
    searchQuery: tableSlice.searchQuery,
    filterModel: tableSlice.filterModel,
    sortModel: tableSlice.sortModel,
    paginationModel: tableSlice.paginationModel,
    setSearch,
    setFilter,
    setSort,
    setPagination,
    reset,
  }), [
    key,
    tableSlice.searchQuery,
    tableSlice.filterModel,
    tableSlice.sortModel,
    tableSlice.paginationModel,
    setSearch,
    setFilter,
    setSort,
    setPagination,
    reset,
  ]);
};

// Re-export reducer for tree-shaking safety (not used directly; store/index imports from slice)
export const __tableStateReducer = tableStateReducer;


